import type { ActionTree, ActionContext, Module, MutationTree } from 'vuex';
import type { AxiosResponse } from 'axios';
import { NearestClubModuleConstant } from '../../constants';
import { mapService } from '../../services/mapService';
import { nearestClubService } from '../../services/nearestClubService';
import type { ltFramework } from '@life-time-inc/enterprise-framework-core';

interface INearestClubState {
	store: ltFramework.INearestClubStoreData;
}

const NearestClubState: INearestClubState = {
	store: {
		queryText: '',
		queryObject: {} as ltFramework.INearestClubSearchActionQuery,
		clubs: [],
		pending: false,
		projectedQuery: {
			latitude: null,
			longitude: null,
			limit: null,
			distance: null
		},
		bounds: {} as ltFramework.IGoogleBoundsObject,
		stateSearch: false,
		initialized: true,
		invalid: false,
		searchSource: ''
	}
};

const { action, mutation } = NearestClubModuleConstant;

const NearestClubMutations: MutationTree<INearestClubState> = {
	[mutation.setStore](state: INearestClubState, payload: ltFramework.INearestClubStoreData) {
		state.store = payload;
	}
};

const NearestClubActions: ActionTree<INearestClubState, ltFramework.IRootState> = {
	[action.searchForNearestClubs](
		{ commit, state }: ActionContext<INearestClubState, ltFramework.IRootState>,
		payload: ltFramework.INearestClubSearchAction
	): Promise<void> {
		const nearestClubQuery: ltFramework.INearestClubQueryObject
			= {} as ltFramework.INearestClubQueryObject;
		const nearestClubStoreData: ltFramework.INearestClubStoreData = {
			queryText: payload.queryText ?? state.store.queryText,
			queryObject: state.store.queryObject,
			clubs: state.store.clubs,
			pending: true,
			projectedQuery: state.store.projectedQuery,
			bounds: state.store.bounds,
			stateSearch: state.store.stateSearch,
			initialized: false,
			invalid: false,
			searchSource: payload.querySource
		};

		if (payload.queryObject) {
			nearestClubStoreData.queryObject = payload.queryObject;
		}

		if (payload.queryObject?.bounds) {
			// assign bounds from query
			nearestClubStoreData.bounds = payload.queryObject.bounds;
		}

		// build query to be used in service call
		if (payload.queryObject?.longitude && payload.queryObject.latitude) {
			nearestClubQuery.latitude = payload.queryObject.latitude;
			nearestClubQuery.longitude = payload.queryObject.longitude;

			nearestClubStoreData.projectedQuery.latitude = payload.queryObject.latitude;
			nearestClubStoreData.projectedQuery.longitude = payload.queryObject.longitude;
		}

		if (payload.queryObject?.stateSearch) {
			nearestClubStoreData.stateSearch = payload.queryObject.stateSearch;
		}

		if (payload.queryLimit) {
			nearestClubQuery.limit = payload.queryLimit;

			nearestClubStoreData.projectedQuery.limit = payload.queryLimit;
		}

		if (payload.queryDistance) {
			nearestClubQuery.distance = payload.queryDistance;

			nearestClubStoreData.projectedQuery.distance = payload.queryDistance;
		}

		if (payload.queryText) {
			return mapService.getGeocoder().then((geocoder: google.maps.Geocoder) => {
				return geocoder
					.geocode(
						{ address: payload.queryText },
						// eslint-disable-next-line @typescript-eslint/no-misused-promises
						(results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
							results = results ?? [];
							if (status === window.google.maps.GeocoderStatus.OK && results.length > 0) {
								nearestClubQuery.latitude = results[0].geometry.location.lat();
								nearestClubQuery.longitude = results[0].geometry.location.lng();
								nearestClubStoreData.projectedQuery = nearestClubQuery;

								if (results[0].geometry.viewport) {
									// build bounds object
									nearestClubStoreData.bounds.sw = results[0].geometry.viewport.getSouthWest();
									nearestClubStoreData.bounds.ne = results[0].geometry.viewport.getNorthEast();
									nearestClubStoreData.stateSearch = results[0].address_components.length <= 2;
								}

								return nearestClubService
									.getNearestClubs(nearestClubQuery, true)
									.then((response: AxiosResponse<ltFramework.IClubFromService[]>) => {
										nearestClubStoreData.clubs = response.data;
										nearestClubStoreData.pending = false;
										nearestClubStoreData.initialized = true;
										nearestClubStoreData.invalid = false;

										commit(mutation.setStore, nearestClubStoreData);
									});
							}
							else {
								nearestClubStoreData.pending = false;
								nearestClubStoreData.initialized = true;
								nearestClubStoreData.invalid = true;
								nearestClubStoreData.clubs = [];

								commit(mutation.setStore, nearestClubStoreData);
							}
						}
					)
					.then(() => {
						return;
					})
					.catch((error: unknown) => {
						// swallowing this error because it seems we get correct UI anyway
						// printing it in case that proves wrong for some input
						console.error('Geocoding error: ', error);
					});
			});
		}
		else {
			return nearestClubService
				.getNearestClubs(nearestClubQuery, true)
				.then((response: AxiosResponse<ltFramework.IClubFromService[]>) => {
					nearestClubStoreData.clubs = response.data;
					nearestClubStoreData.pending = false;
					nearestClubStoreData.initialized = true;
					nearestClubStoreData.invalid = false;

					commit(mutation.setStore, nearestClubStoreData);
				});
		}
	}
};

export const NearestClubModule: Module<INearestClubState, ltFramework.IRootState> = {
	namespaced: true,
	state: NearestClubState,
	actions: NearestClubActions,
	mutations: NearestClubMutations
};
